import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaClock,
  FaEuroSign,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "../HomePage/SectionTitle";

import { getGroupsBySection } from "../../../services/groupServices";

const ORDERED_DAYS = [
  "Lundi",
  "Mardi",
  "Mercredi",
  "Jeudi",
  "Vendredi",
  "Samedi",
  "Dimanche",
];

const parseHoraire = (horaire = "") => {
  const clean = String(horaire).trim();
  const day = ORDERED_DAYS.find((d) => clean.startsWith(d));

  if (!day) return null;

  return {
    day,
    time: clean.replace(day, "").trim(),
  };
};

const parseTimeValue = (time = "") => {
  const match = String(time).match(/(\d{1,2})h?(\d{2})?/i);

  if (!match) return 9999;

  const hours = Number(match[1] || 0);
  const minutes = Number(match[2] || 0);

  return hours * 60 + minutes;
};

const WeeklySchedule = ({ sectionId }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchGroups = async () => {
      try {
        setLoading(true);

        const groupsData = await getGroupsBySection(sectionId);

        if (mounted) {
          setGroups(Array.isArray(groupsData) ? groupsData : []);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des groupes:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (sectionId) fetchGroups();

    return () => {
      mounted = false;
    };
  }, [sectionId]);

  const schedule = useMemo(() => {
    const grouped = {};

    groups.forEach((group) => {
      [group.horaire1, group.horaire2].filter(Boolean).forEach((horaire) => {
        const parsed = parseHoraire(horaire);

        if (!parsed) return;

        if (!grouped[parsed.day]) grouped[parsed.day] = [];

        grouped[parsed.day].push({
          time: parsed.time,
          groupName: group.nom,
          color: group.color_groupe || "bg-primary",
          borderColor:
            group.color_groupe?.replace("bg-", "border-") || "border-primary",
          anneesNaissance: group.annees_naissance,
          tarifGroupe: group.tarif_groupe,
        });
      });
    });

    Object.keys(grouped).forEach((day) => {
      grouped[day].sort((a, b) => parseTimeValue(a.time) - parseTimeValue(b.time));
    });

    return grouped;
  }, [groups]);

  const activeDays = ORDERED_DAYS.filter((day) => schedule[day]?.length > 0);

  if (loading) {
    return (
      <section className="bg-white py-16 dark:bg-slate-900">
        <div className="mx-auto max-w-7xl px-4 text-center text-slate-500 dark:text-slate-300">
          Chargement du planning...
        </div>
      </section>
    );
  }

  if (activeDays.length === 0) return null;

  return (
    <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Planning"
          title="Horaires "
          highlight="des cours"
          subtitle="Consultez les créneaux hebdomadaires, les groupes, les tranches d’âge et les tarifs."
        />

        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {activeDays.map((day, dayIndex) => (
            <Reveal key={day} delay={dayIndex * 0.04}>
              <article className="h-full overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-50 shadow-sm dark:border-white/10 dark:bg-slate-950">
                <div className="flex items-center justify-between border-b border-slate-200 bg-white p-5 dark:border-white/10 dark:bg-slate-900">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wide text-primary">
                      Jour
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                      {day}
                    </h3>
                  </div>

                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                    <FaCalendarAlt />
                  </span>
                </div>

                <div className="space-y-3 p-5">
                  {schedule[day].map((course, index) => (
                    <div
                      key={`${course.groupName}-${index}`}
                      className={[
                        "rounded-2xl border-l-8 p-4 text-white shadow-sm",
                        course.color,
                        course.borderColor,
                      ].join(" ")}
                    >
                      <h4 className="text-lg font-bold">{course.groupName}</h4>

                      <div className="mt-3 grid gap-2 text-sm text-white/90">
                        <p className="flex items-center gap-2">
                          <FaClock />
                          {course.time || "Horaire à préciser"}
                        </p>

                        {course.anneesNaissance && (
                          <p className="flex items-center gap-2">
                            <FaUsers />
                            Années de naissance : {course.anneesNaissance}
                          </p>
                        )}

                        {course.tarifGroupe && (
                          <p className="flex items-center gap-2">
                            <FaEuroSign />
                            Tarif : {course.tarifGroupe} €
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WeeklySchedule;